import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import cobrancaListagensService from "../services/cobrancaListagens.service";
import { formatCurrency, formatDateTime, formatNumber } from "../utils/formatters";

const EMPTY_FILTERS = { status: "", inicioExecucao: "", fimExecucao: "" };
const EMPTY_PAGE = { content: [], totalElements: 0, totalPages: 0 };

const formatDate = (value) => {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
};

const formatNullableCurrency = (value) => value === null || value === undefined ? "—" : formatCurrency(value);
const formatNullableNumber = (value) => value === null || value === undefined ? "—" : formatNumber(value, { maximumFractionDigits: 1 });

const formatDuration = (milliseconds) => {
  if (milliseconds === null || milliseconds === undefined) return "—";
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes} min ${seconds} s` : `${seconds} s`;
};

const getStatusColors = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["OK", "SUCESSO", "CONCLUIDA", "CONCLUÍDA"].includes(normalized)) {
    return { background: "var(--ops-success-bg)", color: "var(--ops-success)" };
  }
  if (["ERRO", "ERROR", "FALHA", "FAILED", "FALHOU"].includes(normalized)) {
    return { background: "var(--ops-danger-bg)", color: "var(--ops-danger)" };
  }
  return { background: "var(--ops-warning-bg)", color: "var(--ops-warning)" };
};

function CargasPagination({ page, pageSize, totalElements, loading, onPageChange, onPageSizeChange }) {
  return (
    <TablePagination
      component="div"
      count={totalElements}
      page={page}
      rowsPerPage={pageSize}
      rowsPerPageOptions={[10, 25, 50, 100]}
      onPageChange={onPageChange}
      onRowsPerPageChange={onPageSizeChange}
      labelRowsPerPage="Linhas por página:"
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      sx={{ borderTop: "1px solid var(--ops-border)", color: "var(--ops-muted)", opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto", "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 } }}
    />
  );
}

export default function CobrancaCargasPage() {
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setCanRetry(false);

    cobrancaListagensService
      .getCargas({ ...appliedFilters, page, size: pageSize })
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((err) => {
        if (!active) return;
        setResult(EMPTY_PAGE);
        setError(cobrancaListagensService.getApiErrorMessage(err, "Não foi possível carregar as execuções de carga."));
        setCanRetry(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [appliedFilters, page, pageSize]);

  const handleFilterChange = (name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    if (draftFilters.inicioExecucao && draftFilters.fimExecucao && draftFilters.inicioExecucao > draftFilters.fimExecucao) {
      setError("A data inicial deve ser anterior ou igual à data final.");
      setCanRetry(false);
      return;
    }
    setError("");
    setCanRetry(false);
    setPage(0);
    setAppliedFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
    setError("");
    setCanRetry(false);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(0);
  };

  return (
    <Stack spacing={1.25}>
      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.75 }, borderRadius: 2, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)" }}>
        <Box component="form" onSubmit={handleApplyFilters}>
          <Stack spacing={1.25}>
            <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 800 }}>
              Histórico de cargas de inadimplência
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.25 }}>
              <TextField
                label="Status"
                value={draftFilters.status}
                onChange={(event) => handleFilterChange("status", event.target.value)}
                placeholder="Ex.: OK"
                size="small"
                fullWidth
              />
              <TextField
                label="Início da execução"
                type="date"
                value={draftFilters.inicioExecucao}
                onChange={(event) => handleFilterChange("inicioExecucao", event.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              <TextField
                label="Fim da execução"
                type="date"
                value={draftFilters.fimExecucao}
                onChange={(event) => handleFilterChange("fimExecucao", event.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button type="button" onClick={handleClearFilters} sx={{ textTransform: "none" }}>
                Limpar filtros
              </Button>
              <Button type="submit" variant="contained" startIcon={<SearchRounded />} sx={{ textTransform: "none", fontWeight: 750 }}>
                Aplicar filtros
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {error ? (
        <Alert
          severity="error"
          action={canRetry ? <Button color="inherit" size="small" onClick={() => { setError(""); setAppliedFilters({ ...appliedFilters }); }}>Tentar novamente</Button> : undefined}
          sx={{ borderRadius: 1.5 }}
        >
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: "var(--ops-surface)", border: "1px solid var(--ops-border)", overflow: "hidden" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: { xs: 1.25, md: 1.75 }, py: 1.25 }}>
          <Typography sx={{ color: "var(--ops-heading)", fontSize: 14, fontWeight: 800 }}>Execuções</Typography>
          <Chip size="small" label={`${formatNumber(result.totalElements)} registros`} sx={{ color: "var(--ops-text)", bgcolor: "var(--ops-subtle)", fontWeight: 700 }} />
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: 220, color: "var(--ops-muted)" }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: 13 }}>Carregando execuções...</Typography>
          </Stack>
        ) : result.content.length === 0 ? (
          <Box sx={{ minHeight: 180, display: "grid", placeItems: "center", px: 2, borderTop: "1px solid var(--ops-border)" }}>
            <Typography sx={{ color: "var(--ops-muted)", fontSize: 13, textAlign: "center" }}>
              {error ? "Não foi possível consultar as cargas." : "Nenhuma execução encontrada para os filtros informados."}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderTop: "1px solid var(--ops-border)" }}>
            <Table size="small" sx={{ minWidth: 1200 }} aria-label="Histórico de cargas de inadimplência">
              <TableHead>
                <TableRow>
                  <TableCell>Data de referência</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Início da execução</TableCell>
                  <TableCell>Fim da execução</TableCell>
                  <TableCell>Duração</TableCell>
                  <TableCell align="right">Valor inadimplente</TableCell>
                  <TableCell align="right">Clientes</TableCell>
                  <TableCell align="right">Títulos</TableCell>
                  <TableCell align="right">Parcelas</TableCell>
                  <TableCell align="right">Atraso médio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.content.map((run) => {
                  const statusColors = getStatusColors(run.status);
                  return (
                    <TableRow key={run.id} hover>
                      <TableCell>{formatDate(run.dataReferencia)}</TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Chip size="small" label={run.status || "Sem status"} sx={{ bgcolor: statusColors.background, color: statusColors.color, fontWeight: 800 }} />
                        {run.errorMessage ? (
                          <Typography sx={{ color: "var(--ops-danger)", fontSize: 11.5, mt: 0.4, maxWidth: 240, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                            {run.errorMessage}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{formatDateTime(run.startedAt)}</TableCell>
                      <TableCell>{formatDateTime(run.finishedAt)}</TableCell>
                      <TableCell>{formatDuration(run.totalMs)}</TableCell>
                      <TableCell align="right">{formatNullableCurrency(run.valorInadimplente)}</TableCell>
                      <TableCell align="right">{run.clientesInadimplentes == null ? "—" : formatNumber(run.clientesInadimplentes)}</TableCell>
                      <TableCell align="right">{run.titulosInadimplentes == null ? "—" : formatNumber(run.titulosInadimplentes)}</TableCell>
                      <TableCell align="right">{run.parcelasInadimplentes == null ? "—" : formatNumber(run.parcelasInadimplentes)}</TableCell>
                      <TableCell align="right">{formatNullableNumber(run.atrasoMedio)}{run.atrasoMedio == null ? "" : " dias"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CargasPagination
          page={page}
          pageSize={pageSize}
          totalElements={result.totalElements}
          loading={loading}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onPageSizeChange={handlePageSizeChange}
        />
      </Paper>
    </Stack>
  );
}
